import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import pageStyles from "./styles/AdminListViewStyles";
import Model from "hooks/Model";
import { returnFormatNumber, verifyEmail } from "utils/validations";
import PlaceholderWin from "assets/images/avatar_win.png";
import { authService, databaseService } from "utils/firebase";
import { TextInput } from "components";
import { CouponProps } from "models";
import Api from "utils/api";
import { Store } from "hooks/main_store";

function AdminListView() {
  const { state } = useContext(Store);
  const styles = pageStyles();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [allCoupons, setAllCoupons] = useState<CouponProps[]>([]);
  const [couponsToRender, setCouponsToRender] = useState<CouponProps[]>([]);

  const redeemCoupon = async (coupon: CouponProps) => {
    try {
      Model.setData("loading", true);
      const response = await Api.post("/redeem/redeemCoupon", {
        clientId: coupon.uid,
        couponRef: coupon.couponRef,
      });

      if (response.ok && response.data === "done") {
        Model.updateAlerts({
          message: "Hemos canjeado el bono con éxito",
          variant: "success",
        });
        await getCouponsByCommerce();
      } else {
        throw new Error(response.data as string);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const onRedeemCoupon = (coupon: CouponProps, isDisabled: boolean) => {
    if (isDisabled) {
      Model.updateAlerts({
        message: "Este cupón ya no es válido",
        variant: "error",
      });
    } else {
      const dataOfDialog = {
        open: true,
        title: "Antes de continuar ...",
        text: `¿Estas de acuerdo en canjear este bono de "${coupon?.name}"? El usuario ya no podrá usarlo.\n\nRecuerda que esta acción no se puede revertir.`,
        txtLeft: "Cancelar",
        fnLeft: () => {
          Model.setData("dialog", { open: false });
        },
        txtRight: "De acuerdo",
        fnRight: () => {
          Model.setData("dialog", { open: false });
          redeemCoupon(coupon);
        },
      };
      Model.setData("dialog", {
        ...dataOfDialog,
      });
    }
  };

  const renderCoupons = () => {
    if (couponsToRender.length < 1) {
      return (
        <p
          style={{
            color: "white",
            fontSize: 18,
            margin: "24px 0",
          }}
        >
          No hay bonos por mostrar
        </p>
      );
    }
    return couponsToRender.map((coupon) => {
      const couponInfo = allCoupons.find((data) => data.id === coupon.couponId);
      return (
        <div className={styles.cardCoupon} key={coupon.couponRef}>
          <img
            className={styles.imgCardCoupon}
            alt="Partner"
            src={couponInfo?.image || PlaceholderWin}
          />
          <div className={styles.infoCardCoupon}>
            <p className={styles.labelCardCoupon}>{couponInfo?.name}</p>
            <p className={styles.priceCardCoupon}>
              {returnFormatNumber(couponInfo?.price)} $FROIN
            </p>
            <p className={styles.emailCardCoupon}>{coupon?.userEmail}</p>
            <button
              className={[
                styles.buttonCardCoupon,
                coupon.status === "redeemed"
                  ? styles.disabledButtonCardCoupon
                  : "",
              ].join(" ")}
              onClick={() =>
                onRedeemCoupon(
                  { ...couponInfo, ...coupon },
                  coupon.status === "redeemed"
                )
              }
            >
              {coupon.status === "redeemed" ? "Redimido" : "Canjear"}
            </button>
          </div>
        </div>
      );
    });
  };

  const getCouponsByCommerce = async () => {
    try {
      Model.setData("loading", true);
      let coupons = [];
      const couponsInfoSnap = await databaseService.getDatabase("coupons");

      couponsInfoSnap.forEach((snap) => {
        coupons.push({
          ...(snap.val() || []),
          id: snap.key,
        });
      });

      setAllCoupons(coupons);

      const couponsSnap = await Api.post("/redeem/getCouponsByCommerce");
      if (couponsSnap.ok && couponsSnap.data) {
        setCouponsToRender(couponsSnap.data as CouponProps[]);
      } else {
        throw new Error(couponsSnap.data as string);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const onAuthSubmit = async () => {
    try {
      Model.setData("loading", true);
      const formatedEmail = email.toLocaleLowerCase().replace(/\s/g, "");

      if (!formatedEmail) {
        throw new Error(`ERROR: ${t("pages.home.buyHome.enterValidEmail")}`);
      } else if (!verifyEmail(formatedEmail)) {
        throw new Error(`ERROR: ${t("pages.home.buyHome.formatInvalid")}`);
      } else {
        const authResponse = await authService.signInWithEmailAndPassword(
          formatedEmail,
          password
        );

        if (authResponse.user.uid) {
          Model.updateInfoUser(authResponse.user);
          Model.updateAlerts({
            message: "Ingreso éxitoso",
            variant: "success",
          });
        } else {
          throw new Error("No existe el usuario");
        }
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  useEffect(() => {
    if (state.infoUser?.uid) {
      getCouponsByCommerce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.infoUser]);

  React.useEffect(() => {
    Model.setData("loading", false);
    Model.setData("opacity", 0.7);
    Model.setData("layoutConfig", {
      withBg: true,
      noheader: true,
      noFooter: true,
    });
    // setTimeout(() => {
    //   setShowCountdown(true);
    // }, 1000);
  }, []);

  return (
    <>
      <h2 className={styles.title}>🎁 Administrador de bonos</h2>

      {state.infoUser?.uid ? (
        renderCoupons()
      ) : (
        <div className={styles.authSection}>
          <TextInput value={email} onChange={setEmail} placeholder="Correo" />
          <TextInput
            value={password}
            onChange={setPassword}
            placeholder="Contraseña"
            type="password"
          />
          <button className={styles.submit} onClick={onAuthSubmit}>
            Ingresar
          </button>
        </div>
      )}

      <div style={{ height: 120 }} />
    </>
  );
}

export default AdminListView;
